import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Clock,
  User,
  Shield,
  AlertTriangle,
  Info,
  Play,
  Pause,
} from "lucide-react";
import { toast } from "sonner";

export function IdleDetectionTest() {
  const {
    user,
    resetIdleTimer,
    getTimeUntilIdle,
    isIdleDetectionEnabled,
    showSessionExpiredModal,
  } = useAuth();
  const [timeUntilIdle, setTimeUntilIdle] = useState<number>(0);
  const [testMode, setTestMode] = useState(false);
  const [testTimeout, setTestTimeout] = useState(30000); // 30 seconds for testing

  // Update idle timer display
  useEffect(() => {
    if (!isIdleDetectionEnabled) return;

    const updateIdleDisplay = () => {
      const timeLeft = getTimeUntilIdle();
      setTimeUntilIdle(timeLeft);
    };

    // Update immediately
    updateIdleDisplay();

    // Update every second for more responsive display
    const interval = setInterval(updateIdleDisplay, 1000);

    return () => clearInterval(interval);
  }, [isIdleDetectionEnabled, getTimeUntilIdle]);

  // Format time until idle
  const formatTimeUntilIdle = (ms: number) => {
    if (ms <= 0) return "Sesión expirará pronto";

    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s restantes`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s restantes`;
    }
    return `${seconds}s restantes`;
  };

  const handleResetTimer = () => {
    resetIdleTimer();
    toast.success("Timer reiniciado", {
      description: "El temporizador de inactividad ha sido reiniciado.",
      duration: 3000,
    });
  };

  const handleTestModeToggle = () => {
    setTestMode(!testMode);
    if (!testMode) {
      toast.info("Modo de prueba activado", {
        description:
          "El temporizador de inactividad está configurado para 30 segundos.",
        duration: 3000,
      });
    } else {
      toast.info("Modo de prueba desactivado", {
        description:
          "El temporizador de inactividad está configurado para 8 horas.",
        duration: 3000,
      });
    }
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Debes iniciar sesión para probar la detección de inactividad.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Prueba de Detección de Inactividad
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                Estado del Usuario:
              </h4>
              <div className="text-sm text-muted-foreground space-y-1">
                <div>
                  Autenticado:{" "}
                  <Badge variant={user ? "default" : "destructive"}>
                    {user ? "Sí" : "No"}
                  </Badge>
                </div>
                <div>Email: {user.email}</div>
                <div>ID: {user.id}</div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Estado de Detección:
              </h4>
              <div className="text-sm text-muted-foreground space-y-1">
                <div>
                  Detección Activa:{" "}
                  <Badge
                    variant={
                      isIdleDetectionEnabled ? "default" : "destructive"
                    }>
                    {isIdleDetectionEnabled ? "Sí" : "No"}
                  </Badge>
                </div>
                <div>
                  Modal Visible:{" "}
                  <Badge
                    variant={
                      showSessionExpiredModal ? "destructive" : "default"
                    }>
                    {showSessionExpiredModal ? "Sí" : "No"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Timer Display */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Tiempo Restante:
            </h4>
            <div className="text-2xl font-bold text-center p-4 bg-muted rounded-lg">
              {formatTimeUntilIdle(timeUntilIdle)}
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleResetTimer}
              variant="outline"
              className="flex items-center gap-2">
              <Play className="h-4 w-4" />
              Reiniciar Timer
            </Button>

            <Button
              onClick={handleTestModeToggle}
              variant={testMode ? "destructive" : "outline"}
              className="flex items-center gap-2">
              {testMode ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {testMode ? "Desactivar Modo Prueba" : "Activar Modo Prueba"}
            </Button>
          </div>

          {/* Test Mode Info */}
          {testMode && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>Modo de Prueba Activo:</strong> El temporizador de
                inactividad está configurado para 30 segundos para facilitar las
                pruebas. En producción, el tiempo de inactividad es de 8 horas.
              </AlertDescription>
            </Alert>
          )}

          {/* Instructions */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Instrucciones:</strong> Para probar la detección de
              inactividad, activa el modo de prueba y luego no interactúes con
              la página durante 30 segundos. El modal de sesión expirada debería
              aparecer automáticamente.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
